import { useState, useEffect } from 'react';
import { useFinance } from '@/contexts/FinanceContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FinanceAccount, AccountType } from '@/types/finance';

const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  checking: 'Conta Corrente',
  savings: 'Poupança',
  wallet: 'Carteira',
  investment: 'Investimento',
  credit_card: 'Cartão de Crédito',
  other: 'Outro',
};

const COLORS = [
  '#1E40AF',
  '#059669',
  '#D97706',
  '#DC2626',
  '#7C3AED',
  '#0891B2',
  '#DB2777',
  '#65A30D',
  '#EA580C',
  '#4F46E5',
  '#113554',
  '#F37B53',
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editAccount?: FinanceAccount;
}

export function AccountModal({ open, onOpenChange, editAccount }: Props) {
  const { addAccount, updateAccount } = useFinance();
  const [name, setName] = useState('');
  const [type, setType] = useState<AccountType>('checking');
  const [initialBalance, setInitialBalance] = useState('');
  const [color, setColor] = useState(COLORS[0]!);
  const [closingDay, setClosingDay] = useState<string>('');
  const [dueDay, setDueDay] = useState<string>('');
  const [creditLimit, setCreditLimit] = useState<string>('');

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (editAccount) {
      setName(editAccount.name);
      setType(editAccount.type);
      setInitialBalance(editAccount.initialBalance.toString().replace('.', ','));
      setColor(editAccount.color);
      setClosingDay(editAccount.closingDay?.toString() ?? '');
      setDueDay(editAccount.dueDay?.toString() ?? '');
      setCreditLimit(
        editAccount.creditLimit !== undefined
          ? editAccount.creditLimit.toString().replace('.', ',')
          : '',
      );
    } else {
      setName('');
      setType('checking');
      setInitialBalance('');
      setColor(COLORS[0]!);
      setClosingDay('');
      setDueDay('');
      setCreditLimit('');
    }
  }, [editAccount, open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const balance = parseFloat(initialBalance.replace(',', '.')) || 0;

    const isCard = type === 'credit_card';
    const closingDayNum = isCard && closingDay ? parseInt(closingDay, 10) : undefined;
    const dueDayNum = isCard && dueDay ? parseInt(dueDay, 10) : undefined;
    const creditLimitNum =
      isCard && creditLimit ? parseFloat(creditLimit.replace(',', '.')) : undefined;

    const cardFields = {
      closingDay: closingDayNum ?? null,
      dueDay: dueDayNum ?? null,
      creditLimit: creditLimitNum ?? null,
    };

    if (editAccount) {
      const diff = balance - editAccount.initialBalance;
      updateAccount(editAccount.id, {
        name,
        type,
        initialBalance: balance,
        currentBalance: editAccount.currentBalance + diff,
        color,
        ...cardFields,
      } as Partial<FinanceAccount>);
    } else {
      addAccount({
        name,
        type,
        initialBalance: balance,
        currentBalance: balance,
        color,
        isActive: true,
        ...(closingDayNum !== undefined && { closingDay: closingDayNum }),
        ...(dueDayNum !== undefined && { dueDay: dueDayNum }),
        ...(creditLimitNum !== undefined && { creditLimit: creditLimitNum }),
      });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="font-brand">
            {editAccount ? 'Editar Conta' : 'Nova Conta'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="acc-name">Nome *</Label>
            <Input
              id="acc-name"
              placeholder="Ex: Conta Corrente Principal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select value={type} onValueChange={(v) => setType(v as AccountType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(ACCOUNT_TYPE_LABELS) as AccountType[]).map((t) => (
                  <SelectItem key={t} value={t}>
                    {ACCOUNT_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="acc-balance">
              {type === 'credit_card' ? 'Saldo da fatura em aberto (R$)' : 'Saldo Inicial (R$)'}
            </Label>
            <Input
              id="acc-balance"
              placeholder="0,00"
              value={initialBalance}
              onChange={(e) => setInitialBalance(e.target.value)}
            />
            {type === 'credit_card' && (
              <p className="text-xs text-muted-foreground">
                Cartão de crédito normalmente começa em 0. Saldo negativo representa fatura em
                aberto.
              </p>
            )}
          </div>

          {type === 'credit_card' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="acc-closing">Dia de fechamento</Label>
                  <Input
                    id="acc-closing"
                    type="number"
                    min={1}
                    max={31}
                    placeholder="Ex: 28"
                    value={closingDay}
                    onChange={(e) => setClosingDay(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="acc-due">Dia de vencimento</Label>
                  <Input
                    id="acc-due"
                    type="number"
                    min={1}
                    max={31}
                    placeholder="Ex: 5"
                    value={dueDay}
                    onChange={(e) => setDueDay(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="acc-limit">Limite (R$)</Label>
                <Input
                  id="acc-limit"
                  placeholder="Opcional"
                  value={creditLimit}
                  onChange={(e) => setCreditLimit(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label>Cor</Label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`h-8 w-8 rounded-full border-2 transition-all ${color === c ? 'scale-110 border-foreground' : 'border-transparent hover:scale-105'}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-primary text-primary-foreground">
              {editAccount ? 'Salvar' : 'Criar Conta'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
